import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { SortDirection } from '@/types/api'

export interface TableStateConfig<F extends Record<string, string>> {
  defaultSort: string
  defaultDirection?: SortDirection
  defaultSize?: number
  /** Filter keys with their empty-state values; also defines the URL params. */
  defaultFilters: F
}

export interface TableState<F extends Record<string, string>> {
  page: number
  size: number
  sort: string
  direction: SortDirection
  filters: F
  activeFilterCount: number
  setPage: (page: number) => void
  setSize: (size: number) => void
  setSort: (sort: string, direction: SortDirection) => void
  setFilter: (key: keyof F, value: string) => void
  resetFilters: () => void
}

/**
 * List state — page, size, sort and filters — kept in the URL.
 *
 * Putting it there rather than in component state means a filtered view can be
 * refreshed, bookmarked, or sent to a colleague, and the browser's back button
 * steps through filter changes the way people expect.
 */
export function useTableState<F extends Record<string, string>>(config: TableStateConfig<F>): TableState<F> {
  const { defaultSort, defaultDirection = 'desc', defaultSize = 20, defaultFilters } = config
  const [searchParams, setSearchParams] = useSearchParams()

  const page = Math.max(0, Number(searchParams.get('page') ?? 0) || 0)
  const size = Math.max(1, Number(searchParams.get('size') ?? defaultSize) || defaultSize)
  const sort = searchParams.get('sort') ?? defaultSort
  const direction: SortDirection = searchParams.get('dir') === 'asc' ? 'asc' : defaultDirection

  const filters = useMemo(() => {
    const resolved = { ...defaultFilters }
    for (const key of Object.keys(defaultFilters) as (keyof F)[]) {
      const value = searchParams.get(String(key))
      if (value !== null) {
        resolved[key] = value as F[keyof F]
      }
    }
    return resolved
    // `defaultFilters` is a literal at each call site; the params are the input
    // that actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const activeFilterCount = useMemo(
    () => (Object.keys(defaultFilters) as (keyof F)[]).filter((key) => filters[key] !== defaultFilters[key]).length,
    [filters, defaultFilters],
  )

  const update = useCallback(
    (mutate: (params: URLSearchParams) => void, replace = false) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current)
          mutate(next)
          return next
        },
        { replace },
      )
    },
    [setSearchParams],
  )

  const setPage = useCallback(
    (nextPage: number) => {
      update((params) => {
        if (nextPage <= 0) params.delete('page')
        else params.set('page', String(nextPage))
      })
    },
    [update],
  )

  const setSize = useCallback(
    (nextSize: number) => {
      update((params) => {
        // Row 200 of the old page size is not row 200 of the new one, so the
        // page index is reset rather than pointing somewhere arbitrary.
        params.delete('page')
        if (nextSize === defaultSize) params.delete('size')
        else params.set('size', String(nextSize))
      })
    },
    [update, defaultSize],
  )

  const setSort = useCallback(
    (nextSort: string, nextDirection: SortDirection) => {
      update((params) => {
        params.delete('page')
        if (nextSort === defaultSort) params.delete('sort')
        else params.set('sort', nextSort)

        if (nextDirection === defaultDirection) params.delete('dir')
        else params.set('dir', nextDirection)
      })
    },
    [update, defaultSort, defaultDirection],
  )

  const setFilter = useCallback(
    (key: keyof F, value: string) => {
      update((params) => {
        // A narrowed list has fewer pages; staying on page 7 would show nothing.
        params.delete('page')
        if (value === defaultFilters[key] || value === '') params.delete(String(key))
        else params.set(String(key), value)
      }, true)
    },
    [update, defaultFilters],
  )

  const resetFilters = useCallback(() => {
    update((params) => {
      params.delete('page')
      for (const key of Object.keys(defaultFilters)) {
        params.delete(key)
      }
    })
  }, [update, defaultFilters])

  return {
    page,
    size,
    sort,
    direction,
    filters,
    activeFilterCount,
    setPage,
    setSize,
    setSort,
    setFilter,
    resetFilters,
  }
}
